import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { DownloadType } from '../../common/utils/downloader/interfaces/downloader.interface';
import { handleOptionDownload, productListPdfHeader } from './utils/downloader.util';
import { ProductCardData } from '../product-card/product-card-display';
import { ProductCatalogDownloadAnalytics } from './product-catalog-download.analytics';
import { SEG } from '../../common/utils/util';
import { StoreState } from '../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { mapProductCardData } from '../search/mapping/search-product-card.mapping';
import { selectResults } from '../search/store/search.selector';

@customElement('mhecomm-product-catalog-download')
export class ProductCatalogDownload extends connect(store)(BaseComponent) {
  /**
   * An input array of product card objects.
   */
  @property({type: Array})
  productData?: ProductCardData[];

  /**
   * An array of headers for CSV/XLSX download.
   */
  @property({type: Array})
  csvXlsxHeaders?: string[] = ['Title', 'Grade', 'Program Id', 'MHID', 'ISBN13', 'Price'];

  /**
   * The prefix to be used for the download label.
   */
  @property()
  downloadLabelPrefix = 'Save as .';

  /**
   * The identifier prefix.
   */
  @property()
  identifier = 'product-download';

  /**
   * An array of download types.
   */
  @property({type: Array})
  downloadTypes?: DownloadType[] = [DownloadType.PDF, DownloadType.CSV, DownloadType.XLSX];

  /**
   * The file name to be used for the download, excluding the file extension.
   */
  @property()
  fileName = 'product-catalog';

  /**
   * An array of product field for CSV and XLSX download.
   */
  @property({type: Array})
  productFields?: string[] = ['title', 'gradeLevels', 'programId', 'mhid', 'isbn13', 'netPrice'];

  /**
   * A state property to hold the product card data, if not passed, products will be fetched from the Redux store.
   */
  @state()
  private products: ProductCardData[] = [];

  /**
   * A state property to track the dropdown's open/closed state.
   */
  @state()
  private isDownloadDropdownOpen = false;

  /**
   * Product catalog download analytics class.
   */
  private analytics: ProductCatalogDownloadAnalytics;

  constructor() {
    super();
    this.analytics = new ProductCatalogDownloadAnalytics();
  }

  stateChanged(state: StoreState): void {
    if (this.productData) {
      this.products = this.productData;
      return;
    }

    this.products = mapProductCardData(selectResults(state) ?? [], SEG);
  }

  renderComp() {
    return html`
      <div id="${this.identifier}-container"
           data-automation-id="${this.identifier}-container"
           class="d-flex flex-column"
      >
        <div class="dropdown d-inline-block" role="group" part="dropdown">
          <button
            id="${this.identifier}-btn"
            data-automation-id="${this.identifier}-btn"
            type="button"
            class="btn btn-primary"
            aria-expanded="${this.isDownloadDropdownOpen}"
            aria-label="Download search results"
            data-bs-toggle="tooltip"
            data-bs-trigger="hover focus"
            data-bs-placement="bottom"
            title="Download search results"
            @click=${(event: any) => this._handleDropdownToggle(event)}
          >
            ${this.uxtIconReady ? html`<uxt-icon icon="download"></uxt-icon>` : nothing}
          </button>

          <ul id="${this.identifier}-option-dropdown"
              aria-labelledby="${this.identifier}-btn"
              class="dropdown-menu end-0"
              part="${this.identifier}-option-menu"
          >
            ${this.downloadTypes?.map(downloadType => html`
              <li part="${this.identifier}-option-list">
                <a
                  id="${this.identifier}-${downloadType}-option"
                  class="dropdown-item"
                  href="#"
                  @click=${() => this._handleOptionClick(downloadType)}
                  part="${this.identifier}-option-link"
                  aria-label="${this.downloadLabelPrefix}${downloadType.toUpperCase()}">
                  ${this.downloadLabelPrefix}${downloadType.toUpperCase()}
                </a>
              </li>
            `)}
          </ul>
        </div>
      </div>
    `;
  }

  /**
   * Opens or closes the dropdown and logs a corresponding event.
   *
   * @param event The event object
   */
  private _handleDropdownToggle(event): void {
    if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) {
      const dropdown = this.getDownloadOptionDropdown();

      this.removeTooltip();
      dropdown.classList.toggle('show');
      this.isDownloadDropdownOpen = dropdown.classList.contains('show');
      this.addEventListener('focusout', () => {
        dropdown.setAttribute('class', 'dropdown-menu end-0');
        this.isDownloadDropdownOpen = false;
      });

      this.analytics.dropdownToggle(this.identifier, this, dropdown);
    }
  }

  /**
   * Logs an event and downloads the file based on the selected option.
   *
   * @param option
   */
  private _handleOptionClick(option: DownloadType): void {
    this.analytics.optionSelection(this.identifier, this, option);

    handleOptionDownload(option, this.fileName, productListPdfHeader(), this.csvXlsxHeaders!, this.productFields!, this.products);
  }

  /**
   * Returns the download option dropdown element.
   */
  private getDownloadOptionDropdown(): HTMLElement {
    return this.shadowRoot!.querySelector(`#${this.identifier}-option-dropdown`) as HTMLElement;
  }

  /**
   * Removes the tooltip from the DOM when the dropdown is expanded.
   */
  private removeTooltip(): void {
    const tooltip = this.shadowRoot?.querySelector('[role="tooltip"]');
    if (tooltip) {
      tooltip.remove();
    }
  }

  /**
   * Initializes the Bootstrap popover.
   */
  updated(): void {
    this.bsInit('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-product-catalog-download': ProductCatalogDownload;
  }
}
